export type OfferId = 'betreut' | 'unbetreut' | 'kids';
export type BookingOfferType = 'Betreut' | 'Nicht Betreut' | 'Kinder (Unbetreut)';
export type PriceUnit = 'hour' | 'day' | 'bubble' | 'field';

export interface OfferPriceOption {
  readonly amount: number;
  readonly unit: PriceUnit;
  readonly duration?: number;
  readonly label: string;
}

export interface OfferExtra {
  readonly description: string;
  readonly amount?: number;
  readonly unit?: PriceUnit;
}

export interface Offer {
  readonly id: OfferId;
  readonly bookingType: BookingOfferType;
  readonly anchor: string;
  readonly title: string;
  readonly summary: string;
  readonly heightRange: string;
  readonly prices: readonly OfferPriceOption[];
  readonly included: readonly string[];
  readonly extras: readonly OfferExtra[];
  readonly maxBubbles: number;
}

export const CURRENCY = 'CHF' as const;

export const OFFERS: readonly Offer[] = [
  {
    id: 'betreut',
    bookingType: 'Betreut',
    anchor: 'betreut',
    title: 'Betreut',
    summary: 'Wir kümmern uns um alles - du brauchst nur anzukommen, zu spielen und Spass zu haben.',
    heightRange: 'ab 1.50m Körpergrösse',
    prices: [
      { amount: 510, unit: 'hour', duration: 1, label: 'für 1 Stunde' },
      { amount: 600, unit: 'hour', duration: 1.5, label: 'für 1.5 Stunden' },
    ],
    included: ['10 Bubbles (ab 1.50m Körpergrösse)', 'Gesamte Organisation', 'Fotos'],
    extras: [
      { description: 'Platz + Garderobe (Sportanlage Rheinauen)', amount: 80, unit: 'field' },
      { description: 'Jede weitere Bubble', amount: 35, unit: 'bubble' },
    ],
    maxBubbles: 24,
  },
  {
    id: 'unbetreut',
    bookingType: 'Nicht Betreut',
    anchor: 'unbetreut',
    title: 'Unbetreut',
    summary: "Du holst die Bubbles bei uns ab, organisierst deinen Platz und legst los. So einfach geht's!",
    heightRange: 'ab 1.50m Körpergrösse',
    prices: [{ amount: 400, unit: 'day', duration: 1, label: 'für 1 Tag' }],
    included: ['10 Bubbles (ab 1.50m Körpergrösse)', 'Jeweils 1 Tag zum Abholen und Zurückbringen'],
    extras: [
      { description: 'Jede weitere Bubble', amount: 35, unit: 'bubble' },
      { description: 'Mehrere Tage auf Anfrage möglich' },
    ],
    maxBubbles: 24,
  },
  {
    id: 'kids',
    bookingType: 'Kinder (Unbetreut)',
    anchor: 'kinder',
    title: 'Unbetreut',
    summary: "Du holst die Bubbles bei uns ab, organisierst deinen Platz und legst los. So einfach geht's!",
    heightRange: 'ab 1.20m bis 1.50m Körpergrösse',
    prices: [{ amount: 350, unit: 'day', duration: 1, label: 'für 1 Tag' }],
    included: ['10 Kinder Bubbles (ab 1.20m bis 1.50m Körpergrösse)', 'Jeweils 1 Tag zum Abholen und Zurückbringen'],
    extras: [
      { description: 'Jede weitere Bubble', amount: 30, unit: 'bubble' },
      { description: 'Mehrere Tage auf Anfrage möglich' },
    ],
    maxBubbles: 24,
  },
];

export const EVENT_TYPES: readonly string[] = [
  'Polteranlass',
  'Teambuilding',
  'Geburtstagsparty',
  'Schulveranstaltung',
  'Firmenfeiern',
  'Vereinsausflug',
  'Grümpelturnier',
  'Familienausflug',
  'Freizeit mit Freunden',
];

const unitText: Record<PriceUnit, string> = {
  hour: 'Stunde',
  day: 'Tag',
  bubble: 'Bubble',
  field: 'Platz',
};

const unitShort: Record<PriceUnit, string> = {
  hour: 'Std',
  day: 'Tag',
  bubble: 'Bubble',
  field: 'Platz',
};

export function getOffer(id: OfferId): Offer {
  const offer = OFFERS.find(o => o.id === id);
  if (!offer) throw new Error(`Unknown offer id: ${id}`);
  return offer;
}

export function formatChf(amount: number): string {
  return `${amount}.-`;
}

export function minPrice(offer: Offer): OfferPriceOption {
  let cheapest = offer.prices[0];

  for (const price of offer.prices) {
    if (price.amount < cheapest.amount) {
      cheapest = price;
    }
  }

  return cheapest;
}

export function priceShort(price: OfferPriceOption): string {
  return `${price.amount} ${CURRENCY}/${unitShort[price.unit]}`;
}

export function priceUnitText(unit: PriceUnit): string {
  return unitText[unit];
}

export function offerStartingAt(offer: Offer): string {
  return `ab ${priceShort(minPrice(offer))}`;
}
